<template>
  <div class="space-y-1">
    <label
      v-if="label"
      :for="id"
      class="block text-sm font-medium text-gray-300"
    >
      {{ label }}
    </label>
    <div class="relative">
      <input
        :id="id"
        :name="name"
        type="tel"
        class="block w-full px-3 py-2 border border-gray-600 rounded-md placeholder-gray-400 bg-gray-700 text-white focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
        :class="{
          'border-red-500 focus:ring-red-500 focus:border-red-500': hasError,
        }"
        :placeholder="placeholder"
        :value="localValue"
        @input="handleInput"
        @keydown="handleKeydown"
        @paste="handlePaste"
        @blur="emit('blur')"
        maxlength="10"
      />
    </div>
    <p v-if="hasError" class="text-red-400 text-xs mt-1 ml-1">{{ error }}</p>
    <p v-else class="text-xs text-gray-400 mt-1 ml-1">{{ helpText }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  id: string;
  name: string;
  label?: string;
  modelValue?: string;
  placeholder?: string;
  error?: string;
  helpText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  modelValue: '',
  placeholder: 'Broj telefona (OPCIONO)',
  error: '',
  helpText: 'Format: 06XXXXXXX (8-10 cifara)',
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  blur: [];
}>();

const hasError = computed((): boolean => {
  return Boolean(props.error);
});

const localValue = computed((): string => {
  // Show the local format (06XXXXXXX) even if stored in international format
  if (props.modelValue.startsWith('+3816')) {
    return '0' + props.modelValue.substring(4); // +3816XXXXXXX -> 06XXXXXXX
  }
  return props.modelValue;
});

const handleKeydown = (event: KeyboardEvent): void => {
  // Dozvoliti specijalne tastere (backspace, delete, tab, escape, enter, home, end, strelice)
  const allowedKeys = [
    'Backspace',
    'Delete',
    'Tab',
    'Escape',
    'Enter',
    'Home',
    'End',
    'ArrowLeft',
    'ArrowUp',
    'ArrowRight',
    'ArrowDown',
  ];

  // Dozvoliti Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
  if (event.ctrlKey || event.metaKey) {
    const allowedCtrlKeys = ['a', 'c', 'v', 'x'];
    if (allowedCtrlKeys.includes(event.key.toLowerCase())) {
      return;
    }
  }

  // Ako je key u dozvoljena lista, ne blokiraj
  if (allowedKeys.includes(event.key)) {
    return;
  }

  // Dozvoliti samo brojevi (0-9)
  if (!/^[0-9]$/.test(event.key)) {
    event.preventDefault();
  }
};

const handlePaste = (event: ClipboardEvent): void => {
  event.preventDefault();
  const paste = event.clipboardData?.getData('text') || '';

  // Uzmi samo brojevi iz paste-ovanog teksta
  const numbersOnly = paste.replace(/\D/g, '');

  if (numbersOnly) {
    const target = event.target as HTMLInputElement;
    const currentValue = target.value.replace(/\D/g, '');

    // Kombinuj trenutni i paste-ovani sadržaj, ograničen na 10 cifara
    const newValue = (currentValue + numbersOnly).substring(0, 10);

    // Ažuriraj input value
    target.value = newValue;

    // Emitiraj update
    let internationalValue = newValue;
    if (newValue.startsWith('06')) {
      internationalValue = '+381' + newValue.substring(1);
    }

    emit('update:modelValue', internationalValue);
  }
};

const handleInput = (event: Event): void => {
  const target = event.target as HTMLInputElement;
  let value = target.value;

  // Remove all non-digit characters
  value = value.replace(/\D/g, '');

  // Limit to max 10 digits
  if (value.length > 10) {
    value = value.substring(0, 10);
  }

  // If empty, emit empty string
  if (value === '') {
    emit('update:modelValue', '');
    return;
  }

  // For display, show the local format (06XXXXXXX)
  target.value = value;

  // For the model value, convert to international format when it starts with 06
  let internationalValue = value;
  if (value.startsWith('06')) {
    internationalValue = '+381' + value.substring(1); // 06XXXXXXX -> +3816XXXXXXX
  }

  emit('update:modelValue', internationalValue);
};
</script>
